import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Toaster } from 'sonner';
import { Footer } from './components/Footer';
import { AboutPage, EditorPage, ProgressPage, LoginPage, VerifyPage, DashboardPage } from './pages';
import { AuthGuard } from './features/auth';
import { RealtimeProvider } from './features/collab/transport/RealtimeProvider';
import { useTypedTranslation, LanguageSwitcher } from './i18n';

// DiagGate & DiagPanel: non-PROD only (staging/dev)
// Using import.meta.env.PROD for compile-time tree-shaking
// Runtime environment check is done inside DiagGate
const LazyDiagRoute = !import.meta.env.PROD
  ? lazy(() =>
      import('./diag').then((m) => ({
        default: () => (
          <m.DiagGate>
            <m.DiagPanel />
          </m.DiagGate>
        ),
      }))
    )
  : null;

export function App() {
  const { t } = useTypedTranslation();
  return (
    <BrowserRouter>
      <RealtimeProvider>
        <Toaster position="top-center" richColors />
        {/* Global LanguageSwitcher - visible on all pages (SPEC-017 Task 4.2) */}
        <div className="fixed top-4 right-4 z-50">
          <LanguageSwitcher variant="compact" />
        </div>
        <Routes>
          <Route path="/" element={<div>{t('app.home')}</div>} />
          <Route path="/about" element={<AboutPage />} />
          {/* Auth routes - @see SPEC-005.5 Requirements 1.1, 2.1 */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/auth/verify" element={<VerifyPage />} />
          {/* Protected routes - @see SPEC-005.5 Requirements 5.2 */}
          <Route
            path="/dashboard"
            element={
              <AuthGuard>
                <DashboardPage />
              </AuthGuard>
            }
          />
          <Route
            path="/editor"
            element={
              <AuthGuard>
                <EditorPage />
              </AuthGuard>
            }
          />
          <Route
            path="/editor/:docId"
            element={
              <AuthGuard>
                <EditorPage />
              </AuthGuard>
            }
          />
          <Route path="/progress" element={<ProgressPage />} />
          {/* __diag route: staging/dev only, tree-shaken in production */}
          {!import.meta.env.PROD && LazyDiagRoute && (
            <Route
              path="/__diag"
              element={
                <Suspense fallback={<div>{t('common.loading')}</div>}>
                  <LazyDiagRoute />
                </Suspense>
              }
            />
          )}
        </Routes>
        <Footer />
        {/* DevPanel: DEV only - completely excluded from production via vite plugin */}
        {import.meta.env.DEV && <DevPanelLoader />}
      </RealtimeProvider>
    </BrowserRouter>
  );
}

// Separate component to isolate the dynamic import
// This allows vite plugin to intercept and replace the import in production
function DevPanelLoader() {
  const LazyDevPanel = lazy(() => import('./devpanel/DevPanel'));
  return (
    <Suspense fallback={null}>
      <LazyDevPanel />
    </Suspense>
  );
}
